import type {ReactNode} from 'react'
import {Fragment, useState} from 'react'
import invariant from 'invariant'
import fill from 'lodash/fill'
import flatten from 'lodash/flatten'
import isEqual from 'lodash/isEqual'
import {exhaustive} from '@freckle/exhaustive'
import {mthen, maybe} from '@freckle/maybe'
import type {NonEmptyArray} from '@freckle/non-empty'
import {mapOnNonEmpty, nonEmptyToArray} from '@freckle/non-empty'
import {useSafeEffectExtraDeps} from '@freckle/react-hooks'
import {safeArrayAccess} from '@freckle/educator-entities/ts/common/helpers/array'
import {mutateAtIndex} from '@freckle/educator-entities/ts/common/helpers/common-helper'
import type {FillInTheBlanksCellAnswerT} from '@freckle/educator-entities/ts/math/common/logic/answer-types'
import type {MathQuestionAttrs} from '@freckle/educator-entities/ts/math/common/models/questions'
import type {
  MathQuestionFillInTheBlanksMetadataAttrs,
  Rows,
  Answer,
  Text as TextColumn,
  CorrectAnswers
} from '@freckle/educator-entities/ts/math/common/models/questions/question-fill-in-the-blanks'
import {Dropdown} from '@freckle/educator-materials/ts/components/dropdown'
import {EnableMathJax} from '@freckle/educator-materials/ts/components/enable-math-jax'
import {Flex} from '@freckle/educator-materials/ts/components/flex'
import {Text} from '@freckle/educator-materials/ts/components/typography/text'
import type {ResponsiveBreakpointKey} from '@freckle/educator-materials/ts/helpers/responsive-helper'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {useResponsiveInfo} from '@freckle/educator-materials/ts/hooks/use-responsive-info'
import {
  tableWrapper,
  table,
  rowNumberHeader,
  textLeft,
  textRight,
  inColumnHeader,
  tableWrapperNoSubmission
} from './fill-in-the-blanks.module.scss'

type Props = {
  question: Pick<MathQuestionAttrs, 'id'>
  metadata: MathQuestionFillInTheBlanksMetadataAttrs
  revealCorrectAnswers?: boolean
}

type AnswerWithIndex = {
  row: number
  column: number
  answerIndex: number
} & Answer

type RowsWithIndexedAnswers = NonEmptyArray<NonEmptyArray<AnswerWithIndex | TextColumn>>

type UnAnsweredStudentAnswer = {
  tag: 'unanswered'
}

type AnsweredStudentAnswer = {
  tag: 'answered'
  answer: FillInTheBlanksCellAnswerT
}

type StudentAnswer = UnAnsweredStudentAnswer | AnsweredStudentAnswer

function mkRowsWithIndexedAnswers(rows: Rows): RowsWithIndexedAnswers {
  let answerIndex = 0
  const mappingFunction = (columns: NonEmptyArray<TextColumn | Answer>, rowIndex: number) => {
    const nestedMappingFunction = (column: TextColumn | Answer, colIndex: number) =>
      column.tag === 'answers'
        ? {
            ...column,
            row: rowIndex,
            column: colIndex,
            answerIndex: answerIndex++
          }
        : column

    const result = mapOnNonEmpty(columns, nestedMappingFunction)
    return result
  }
  return mapOnNonEmpty(rows, mappingFunction)
}

const NO_SELECTION_VALUE = 'not-selected'

function studentAnswerFromSelection(selection: string, row: number, column: number): StudentAnswer {
  return selection === NO_SELECTION_VALUE
    ? emptyStudentAnswer()
    : {tag: 'answered', answer: {row, column, answer: selection}}
}

function emptyStudentAnswer(): StudentAnswer {
  return {tag: 'unanswered'}
}

function defaultAnswers(numQuestions: number): Array<StudentAnswer> {
  return fill(new Array(numQuestions), emptyStudentAnswer())
}

type StudentResponseChangesTracker = {
  [answerIndex: number]: boolean
}

const defaultStudentResponseChangesTracker = (
  allColumns: Array<AnswerWithIndex | TextColumn>
): StudentResponseChangesTracker =>
  allColumns.reduce(
    (tracker: StudentResponseChangesTracker, column: AnswerWithIndex | TextColumn) => {
      switch (column.tag) {
        case 'answers':
          tracker[column.answerIndex] = false
          break
        case 'text-only':
          break
        default:
          exhaustive(column)
      }
      return tracker
    },
    {}
  )

export function MathQuestionFillInTheBlanks({
  question,
  metadata: {tableNumberingEnabled, header, rows, correctAnswers},
  revealCorrectAnswers
}: Props) {
  const {t} = useSafeTranslation()
  const responsiveBreakpoint = useResponsiveInfo()

  const renderLayout = responsiveBreakpoint

  const rowsWithIndexedAnswers = mkRowsWithIndexedAnswers(rows)
  const allColumns = flatten(rowsWithIndexedAnswers)
  const numQuestions = allColumns.filter(column => column.tag === 'answers').length

  const [studentAnswers, setStudentAnswers] = useState<Array<StudentAnswer>>(
    defaultAnswers(numQuestions)
  )

  const [showInlineCorrectnessFeedback, setShowInlineCorrectnessFeedback] = useState(false)
  const [studentResponseChangesTracker, setStudentResponseChangesTracker] = useState(
    defaultStudentResponseChangesTracker(allColumns)
  )

  useSafeEffectExtraDeps<{allColumns: Array<AnswerWithIndex | TextColumn>}>(
    ({allColumns}) => {
      setShowInlineCorrectnessFeedback(false)
      setStudentAnswers(defaultAnswers(numQuestions))
      setStudentResponseChangesTracker(defaultStudentResponseChangesTracker(allColumns))
    },
    [question.id, numQuestions],
    {
      allColumns: {
        value: allColumns,
        comparator: isEqual
      }
    }
  )

  const setAnswerAtIndex = (answer: string, index: number, row: number, column: number) => {
    setStudentAnswers(answers =>
      mutateAtIndex(answers, index, () => studentAnswerFromSelection(answer, row, column))
    )
    // We want to clear inline feedback only for the answer that has changed.
    // This is important for how we aid screen readers. We want to preserve the
    // inline feedback for unchanged answers so that they can hear it when they
    // focus on it.
    setStudentResponseChangesTracker(prevTracker => ({
      ...prevTracker,
      [index]: true
    }))
  }

  return (
    <Flex flexDirection="column" justifyContent="space-between" flex="1">
      <div>
        <ScreenReaderTotalIncorrectAnswerNotification
          shouldInjectFeedback={showInlineCorrectnessFeedback}
          studentAnswers={studentAnswers}
          correctAnswers={correctAnswers}
        />
        <div className={`${tableWrapper} ${tableWrapperNoSubmission}`}>
          <table className={table}>
            <caption className="sr-only">{t('FILL_IN_THE_BLANKS_TABLE_CAPTION_SR_ONLY')}</caption>
            <TableHeader
              header={header}
              tableNumberingEnabled={tableNumberingEnabled}
              layout={renderLayout}
            />
            <TableRows
              header={header}
              rows={rowsWithIndexedAnswers}
              tableNumberingEnabled={tableNumberingEnabled}
              layout={renderLayout}
              setAnswerAtIndex={setAnswerAtIndex}
              revealCorrectAnswers={revealCorrectAnswers === true}
              correctAnswers={correctAnswers}
              studentAnswers={studentAnswers}
              showInlineCorrectnessFeedback={showInlineCorrectnessFeedback}
              studentResponseChangesTracker={studentResponseChangesTracker}
            />
          </table>
        </div>
      </div>
    </Flex>
  )
}

function ScreenReaderTotalIncorrectAnswerNotification({
  shouldInjectFeedback,
  studentAnswers,
  correctAnswers
}: {
  shouldInjectFeedback: boolean
  studentAnswers: StudentAnswer[]
  correctAnswers: CorrectAnswers
}) {
  const {t} = useSafeTranslation()
  const studentHasUnAnsweredQuestions = doesStudentHaveUnAnsweredQuestions(studentAnswers)
  const liveRegionId = 'total-incorrect-answers-notification'

  if (studentHasUnAnsweredQuestions || !shouldInjectFeedback) {
    return <ScreenReaderLiveRegionWrapper id={liveRegionId} />
  }

  const arrayCorrectAnswers = nonEmptyToArray(correctAnswers)
  const numIncorrect = studentAnswers.filter((studentAnswer, answerIndex: number) => {
    invariant(studentAnswer.tag === 'answered', `unexpected unanswered answer`)
    const correctAnswerAtIndex = safeArrayAccess(arrayCorrectAnswers, answerIndex)
    return correctAnswerAtIndex === undefined
      ? false
      : !isStudentAnswerCorrect(correctAnswerAtIndex, studentAnswer)
  }).length
  return numIncorrect > 0 ? (
    <ScreenReaderLiveRegionWrapper id={liveRegionId}>
      {t('FILL_IN_THE_BLANKS_TOTAL_INCORRECT_ANSWERS_NOTIFICATION_SR_ONLY', {numIncorrect})}
    </ScreenReaderLiveRegionWrapper>
  ) : (
    <ScreenReaderLiveRegionWrapper id={liveRegionId} />
  )
}

function ScreenReaderLiveRegionWrapper({id, children}: {id: string; children?: ReactNode}) {
  // The children prop is optional but we will always render the `<div aria-live...>`
  // because screen readers require the presence of live regions on
  // page load so that they can add a listener on them.
  return (
    <div id={id} className="sr-only" aria-live="polite">
      {children}
    </div>
  )
}

function TableHeader({
  header,
  tableNumberingEnabled,
  layout
}: {
  header: NonEmptyArray<string> | undefined | null
  tableNumberingEnabled: boolean
  layout: ResponsiveBreakpointKey
}) {
  const {t} = useSafeTranslation()

  if (header === null || header === undefined || layout === 'phoneVertical') {
    return <></>
  }

  return (
    <thead>
      <tr>
        {tableNumberingEnabled ? (
          <th scope="col" className={rowNumberHeader}>
            <span className="sr-only">
              {t('FILL_IN_THE_BLANKS_TABLE_ROW_NUMBER_HEADER_SR_ONLY')}
            </span>
          </th>
        ) : null}
        {mapOnNonEmpty(header, (head: string, i: number) => (
          <th key={i} scope="col">
            <Text style="subtitle-1" htmlElement="span" modifiers={['inherit-color']}>
              {head}
            </Text>
          </th>
        ))}
      </tr>
    </thead>
  )
}

function TableRows({
  rows,
  tableNumberingEnabled,
  layout,
  header,
  revealCorrectAnswers,
  correctAnswers,
  studentAnswers,
  setAnswerAtIndex,
  showInlineCorrectnessFeedback,
  studentResponseChangesTracker
}: {
  rows: RowsWithIndexedAnswers
  tableNumberingEnabled: boolean
  layout: ResponsiveBreakpointKey
  header: NonEmptyArray<string> | undefined | null
  revealCorrectAnswers: boolean
  correctAnswers: CorrectAnswers
  studentAnswers: StudentAnswer[]
  showInlineCorrectnessFeedback: boolean
  setAnswerAtIndex: (answer: string, index: number, row: number, column: number) => void
  studentResponseChangesTracker: StudentResponseChangesTracker
}) {
  return (
    <tbody>
      {mapOnNonEmpty(rows, (columns: NonEmptyArray<AnswerWithIndex | TextColumn>, i: number) => (
        <tr key={i}>
          {tableNumberingEnabled ? (
            <th scope="row" className={rowNumberHeader}>
              <Text htmlElement="span" style="subtitle-1">
                {indexToRowNumber(i)}
              </Text>
            </th>
          ) : null}
          {layout === 'phoneVertical' ? (
            <SingleColumnLayout
              columns={columns}
              header={header}
              setAnswerAtIndex={setAnswerAtIndex}
              revealCorrectAnswers={revealCorrectAnswers}
              correctAnswers={correctAnswers}
              studentAnswers={studentAnswers}
              showInlineCorrectnessFeedback={showInlineCorrectnessFeedback}
              studentResponseChangesTracker={studentResponseChangesTracker}
            />
          ) : (
            <MultiColumnLayout
              columns={columns}
              setAnswerAtIndex={setAnswerAtIndex}
              revealCorrectAnswers={revealCorrectAnswers}
              correctAnswers={correctAnswers}
              studentAnswers={studentAnswers}
              showInlineCorrectnessFeedback={showInlineCorrectnessFeedback}
              studentResponseChangesTracker={studentResponseChangesTracker}
            />
          )}
        </tr>
      ))}
    </tbody>
  )
}

function MultiColumnLayout({
  columns,
  revealCorrectAnswers,
  correctAnswers,
  studentAnswers,
  showInlineCorrectnessFeedback,
  setAnswerAtIndex,
  studentResponseChangesTracker
}: {
  columns: NonEmptyArray<AnswerWithIndex | TextColumn>
  revealCorrectAnswers: boolean
  correctAnswers: CorrectAnswers
  studentAnswers: StudentAnswer[]
  showInlineCorrectnessFeedback: boolean
  setAnswerAtIndex: (answer: string, index: number, row: number, column: number) => void
  studentResponseChangesTracker: StudentResponseChangesTracker
}) {
  const content = mapOnNonEmpty(columns, (column: AnswerWithIndex | TextColumn, i: number) => (
    <td key={i}>
      <ColumnContent
        column={column}
        setAnswerAtIndex={setAnswerAtIndex}
        revealCorrectAnswers={revealCorrectAnswers}
        correctAnswers={correctAnswers}
        studentAnswers={studentAnswers}
        showInlineCorrectnessFeedback={showInlineCorrectnessFeedback}
        studentResponseChangesTracker={studentResponseChangesTracker}
      />
    </td>
  ))
  return <>{content}</>
}

function SingleColumnLayout({
  columns,
  header,
  revealCorrectAnswers,
  correctAnswers,
  studentAnswers,
  showInlineCorrectnessFeedback,
  setAnswerAtIndex,
  studentResponseChangesTracker
}: {
  columns: NonEmptyArray<AnswerWithIndex | TextColumn>
  header: NonEmptyArray<string> | undefined | null
  revealCorrectAnswers: boolean
  correctAnswers: CorrectAnswers
  studentAnswers: StudentAnswer[]
  showInlineCorrectnessFeedback: boolean
  setAnswerAtIndex: (answer: string, index: number, row: number, column: number) => void
  studentResponseChangesTracker: StudentResponseChangesTracker
}) {
  return (
    <td>
      {mapOnNonEmpty(columns, (column: AnswerWithIndex | TextColumn, i: number) => (
        <Fragment key={i}>
          {header !== null && header !== undefined ? (
            <Text style="subtitle-3" className={inColumnHeader}>
              {header[i]}
            </Text>
          ) : null}
          <ColumnContent
            key={i}
            column={column}
            setAnswerAtIndex={setAnswerAtIndex}
            revealCorrectAnswers={revealCorrectAnswers}
            correctAnswers={correctAnswers}
            studentAnswers={studentAnswers}
            showInlineCorrectnessFeedback={showInlineCorrectnessFeedback}
            studentResponseChangesTracker={studentResponseChangesTracker}
          />
        </Fragment>
      ))}
    </td>
  )
}

function ColumnContent({
  column,
  revealCorrectAnswers,
  correctAnswers,
  studentAnswers,
  showInlineCorrectnessFeedback,
  setAnswerAtIndex,
  studentResponseChangesTracker
}: {
  column: AnswerWithIndex | TextColumn
  revealCorrectAnswers: boolean
  correctAnswers: CorrectAnswers
  studentAnswers: StudentAnswer[]
  showInlineCorrectnessFeedback: boolean
  setAnswerAtIndex: (answer: string, index: number, row: number, column: number) => void
  studentResponseChangesTracker: StudentResponseChangesTracker
}) {
  switch (column.tag) {
    case 'answers':
      return (
        <>
          {mthen(column.leftText, text => (
            <Text style="practice-sm" className={textLeft}>
              {text}
            </Text>
          ))}
          <SelectorOrCorrectAnswer
            revealCorrectAnswers={revealCorrectAnswers}
            correctAnswers={correctAnswers}
            setAnswerAtIndex={setAnswerAtIndex}
            showInlineCorrectnessFeedback={showInlineCorrectnessFeedback}
            column={column}
            studentAnswers={studentAnswers}
            studentResponseChangesTracker={studentResponseChangesTracker}
          />
          {mthen(column.rightText, text => (
            <Text style="practice-sm" className={textRight}>
              {text}
            </Text>
          ))}
        </>
      )
    case 'text-only':
      return (
        <Text style="practice-sm" htmlElement="span">
          {column.text}
        </Text>
      )
    default:
      return exhaustive(column)
  }
}

type FeedbackState =
  | {
      tag: 'shown'
      correct: boolean
      studentAnswer: AnsweredStudentAnswer
    }
  | {
      tag: 'not-shown'
    }

function SelectorOrCorrectAnswer({
  revealCorrectAnswers,
  setAnswerAtIndex,
  correctAnswers,
  studentAnswers,
  column,
  showInlineCorrectnessFeedback,
  studentResponseChangesTracker
}: {
  column: AnswerWithIndex
  revealCorrectAnswers: boolean
  correctAnswers: CorrectAnswers
  studentAnswers: StudentAnswer[]
  showInlineCorrectnessFeedback: boolean
  setAnswerAtIndex: (answer: string, index: number, row: number, column: number) => void
  studentResponseChangesTracker: StudentResponseChangesTracker
}) {
  const answerIndex = column.answerIndex
  const row = column.row
  const col = column.column

  const studentAnswerAtIndex = safeArrayAccess(studentAnswers, answerIndex)

  const mkFeedbackState = (shouldShow: boolean): FeedbackState => {
    return showInlineCorrectnessFeedback &&
      studentResponseChangesTracker[answerIndex] === false &&
      studentAnswerAtIndex !== undefined &&
      studentAnswerAtIndex.tag === 'answered' &&
      shouldShow
      ? {
          tag: 'shown',
          correct: isAnswerCorrectAtIndex(correctAnswers, studentAnswers, answerIndex),
          studentAnswer: studentAnswerAtIndex
        }
      : {tag: 'not-shown'}
  }

  const feedbackOnFocus = mkFeedbackState(false)

  return revealCorrectAnswers ? (
    <>{correctAnswers[answerIndex]}</>
  ) : (
    <>
      <Dropdown
        handleChange={a => {
          if (a === null) return
          setAnswerAtIndex(a.value, answerIndex, row, col)
        }}
        value={null}
        options={{
          tag: 'flat',
          options: mapOnNonEmpty(column.options, (option: string) => ({
            label: option,
            value: option
          }))
        }}
        formatOptionLabel={option => (
          <EnableMathJax
            key={maybe(
              () => 'unanswered',
              a => (a.tag === 'answered' ? a.answer.answer : a.tag),
              studentAnswerAtIndex
            )}
          >
            {option.label}
          </EnableMathJax>
        )}
      />

      <ScreenReaderAnswerCorrectnessHelpText
        answerIndex={answerIndex}
        feedbackState={feedbackOnFocus}
      />
    </>
  )
}

function ScreenReaderAnswerCorrectnessHelpText({
  answerIndex,
  feedbackState
}: {
  answerIndex: number
  feedbackState: FeedbackState
}) {
  return (
    <ScreenReaderLiveRegionWrapper id={mkAnswerCorrectnessHelpTextId(answerIndex)}>
      <AnswerCorrectnessHelpText feedbackState={feedbackState} />
    </ScreenReaderLiveRegionWrapper>
  )
}

function AnswerCorrectnessHelpText({feedbackState}: {feedbackState: FeedbackState}) {
  const {t} = useSafeTranslation()
  switch (feedbackState.tag) {
    case 'shown':
      return feedbackState.correct ? (
        <>
          {t('FILL_IN_THE_BLANKS_CORRECT_ANSWER_NOTIFICATION_SR_ONLY', {
            answer: feedbackState.studentAnswer.answer.answer
          })}
        </>
      ) : (
        <>
          {t('FILL_IN_THE_BLANKS_INCORRECT_ANSWER_NOTIFICATION_SR_ONLY', {
            answer: feedbackState.studentAnswer.answer.answer
          })}
        </>
      )
    case 'not-shown':
      return <></>
    default:
      return exhaustive(feedbackState)
  }
}

function mkAnswerCorrectnessHelpTextId(answerIndex: number): string {
  return `answer-${answerIndex}-help-text`
}

const doesStudentHaveUnAnsweredQuestions = (studentAnswers: StudentAnswer[]): boolean =>
  studentAnswers.some(answer => answer.tag !== 'answered')

function isAnswerCorrectAtIndex(
  correctAnswers: CorrectAnswers,
  studentAnswers: StudentAnswer[],
  index: number
): boolean {
  const studentAnswerAtIndex = safeArrayAccess(studentAnswers, index)
  const correctAnswerAtIndex = safeArrayAccess(correctAnswers, index)
  if (studentAnswerAtIndex === undefined || correctAnswerAtIndex === undefined) {
    return false
  }

  switch (studentAnswerAtIndex.tag) {
    case 'unanswered':
      return false
    case 'answered':
      return isStudentAnswerCorrect(correctAnswerAtIndex, studentAnswerAtIndex)
    default:
      return exhaustive(studentAnswerAtIndex)
  }
}

function isStudentAnswerCorrect(
  correctAnswer: string,
  studentAnswer: AnsweredStudentAnswer
): boolean {
  return correctAnswer === studentAnswer.answer.answer
}

function indexToRowNumber(i: number): number {
  return i + 1
}
